/*
 * /light-characteristics — расшифровка характеристики огня и знаки МАМС.
 *
 * Три режима: разбор произвольной записи с карты, справочник знаков и
 * тренажёр опознания по ритму. Общее у всех трёх — живой ритм: маяк опознают
 * по нему, и справочник, который его не показывает, объясняет всё, кроме
 * главного.
 */

import { parseCharacteristic, stateAt, flashesPerMinute } from './parse.js';
import { MARKS, BY_ID, characteristicFor, bodyFor, regionMatters } from './marks.js';
import { drawMark, drawLamp, drawRhythm, PALETTE } from './draw.js';
import { horizonNm, geographicRangeNm, luminousRangeNm } from '../maritime/geodesy.js';
import { makeT } from '../maritime/i18n.js';

const B = window.__LIGHTCHAR || { T: {}, M: {}, C: {}, cfg: {} };
const T = B.T, MT = B.M, CT = B.C;
const t = makeT(T);
const $ = id => document.getElementById(id);
const el = (tag, cls, text) => { const n = document.createElement(tag); if (cls) n.className = cls; if (text != null) n.textContent = text; return n; };

const S = {
    mode: 'decode',
    region: 'A',
    night: true,
    input: 'Fl(3)WR.15s12M',
    parsed: null,
    eye: 4,
    metVis: 10,
    markId: 'cardinal-e',
    quiz: null,
    quizStats: { asked: 0, right: 0 },
    raf: 0,
    t0: performance.now(),
};

/* ── цикл анимации: только пока вкладка видима и есть что мигать ─────────── */
function frame() {
    S.raf = 0;
    const now = performance.now() - S.t0;
    if (S.mode === 'decode' && S.parsed?.ok) {
        drawLamp($('lcLight'), stateAt(S.parsed, now));
        drawRhythm($('lcRhythm'), S.parsed, now);
    }
    if (S.mode === 'marks') {
        for (const c of document.querySelectorAll('#lcMarkGrid canvas')) {
            const mk = BY_ID[c.dataset.id];
            const p = c._parsed || (c._parsed = parseCharacteristic(characteristicFor(mk, S.region)));
            drawMark(c, mk, S.region, p.ok ? stateAt(p, now) : { on: true, colour: 'W' }, { night: S.night });
        }
    }
    if (S.mode === 'quiz' && S.quiz) {
        const p = S.quiz.parsed;
        drawMark($('lcQuizLight'), S.quiz.mark, S.quiz.region,
            p.ok ? stateAt(p, now) : { on: true, colour: 'W' },
            { night: true });
        if (S.quiz.revealed) drawRhythm($('lcQuizRhythm'), p, now);
    }
    if (!document.hidden) S.raf = requestAnimationFrame(frame);
}
function kick() { if (!S.raf) S.raf = requestAnimationFrame(frame); }
document.addEventListener('visibilitychange', () => { if (!document.hidden) kick(); });

/* ═══ РАСШИФРОВКА ════════════════════════════════════════════════════════ */
const CLASS_KEY = { F: 'F', FL: 'Fl', LFL: 'LFl', ISO: 'Iso', OC: 'Oc', Q: 'Q', VQ: 'VQ', UQ: 'UQ', IQ: 'IQ', IVQ: 'IVQ', IUQ: 'IUQ', MO: 'Mo', AL: 'Al', FFL: 'FFl' };

function describe(p) {
    const out = [];
    for (const ph of p.phases) {
        const key = CLASS_KEY[ph.cls.toUpperCase()] || ph.cls;
        let s = t('cls_' + key);
        if (ph.group) {
            s += ' ' + (ph.group.length > 1
                ? t('group_composite', { list: ph.group.join(' + ') })
                : t('group_n', { n: ph.group[0] }));
        }
        if (ph.morse) s += ' ' + t('morse_letter', { l: ph.morse });
        out.push(s);
    }
    let text = out.join(t('then'));
    const cols = p.colours.map(c => t('col_' + c) || c);
    if (p.alternating) text += ', ' + t('alternating_colours', { list: cols.join(' / ') });
    else if (p.sectors) text += ', ' + t('sector_colours', { list: cols.join(', ') });
    else text += ', ' + t('colour_one', { c: cols[0] });
    if (p.periodS) text += ', ' + t('period_s', { n: fmt(p.periodS) });
    if (p.directional) text += ', ' + t('is_directional');
    return text;
}

const fmt = n => (Math.round(n * 100) / 100).toString().replace('.', ',');

function renderDecode() {
    const p = S.parsed = parseCharacteristic(S.input);
    const box = $('lcResult');
    box.textContent = '';

    if (!p.ok) {
        box.append(el('p', 'lc-bad', t('err_' + (p.error || 'unknown'), { s: p.bad || S.input })));
        $('lcFacts').textContent = '';
        $('lcRange').hidden = true;
        return;
    }

    box.append(el('p', 'lc-plain', describe(p)));

    // Факты в отдельных карточках — их читают глазами по одному.
    const facts = $('lcFacts');
    facts.textContent = '';
    const addFact = (label, value, sub) => {
        const f = el('div', 'tc-stat');
        f.append(el('div', 'tc-stat-l', label), el('div', 'tc-stat-v', value));
        if (sub) f.append(el('div', 'tc-stat-s', sub));
        facts.append(f);
    };
    if (p.periodS) addFact(t('f_period'), fmt(p.periodS) + ' ' + t('unit_s'));
    const fpm = flashesPerMinute(p);
    if (fpm && p.phases.some(ph => /^(Q|VQ|UQ|IQ|IVQ|IUQ)$/i.test(ph.cls)))
        addFact(t('f_rate'), Math.round(fpm) + '', t('f_rate_sub'));
    if (p.rangeNm) addFact(t('f_range'), p.rangeNm + ' ' + t('unit_nm'), t('f_range_sub'));
    if (p.elevationM) addFact(t('f_elev'), p.elevationM + ' ' + t('unit_m'));
    addFact(t('f_colours'), p.colours.map(c => t('col_' + c) || c).join(', '));

    for (const w of p.warnings) {
        const wr = el('p', 'lc-warn', t('warn_' + w));
        if (wr.textContent && wr.textContent !== 'warn_' + w) box.append(wr);
    }
    if (p.reconstructed) box.append(el('p', 'tc-note', t('reconstructed_note')));

    renderRange(p);
    kick();
}

function renderRange(p) {
    const wrap = $('lcRange');
    if (!p.rangeNm && !p.elevationM) { wrap.hidden = true; return; }
    wrap.hidden = false;
    const H = p.elevationM ?? 20;
    const geo = geographicRangeNm(S.eye, H);
    const lum = p.rangeNm ? luminousRangeNm(p.rangeNm, S.metVis) : null;
    const eff = lum == null ? geo : Math.min(geo, lum);

    $('lcRangeOut').textContent = '';
    const row = (k, v, note) => {
        const r = el('div', 'lc-rangerow');
        r.append(el('span', 'lc-rk', k), el('span', 'lc-rv', v));
        if (note) r.append(el('span', 'lc-rn', note));
        $('lcRangeOut').append(r);
    };
    row(t('r_geographic'), fmt(geo) + ' ' + t('unit_nm'),
        t('r_geographic_note', { eye: S.eye, h: fmt(H), horizon: fmt(horizonNm(S.eye)) }));
    if (lum != null) row(t('r_luminous'), fmt(lum) + ' ' + t('unit_nm'), t('r_luminous_note', { vis: S.metVis }));
    row(t('r_effective'), fmt(eff) + ' ' + t('unit_nm'),
        lum != null && lum < geo ? t('r_limited_by_light') : t('r_limited_by_horizon'));
}

/* ═══ ЗНАКИ ══════════════════════════════════════════════════════════════ */
function renderMarks() {
    const grid = $('lcMarkGrid');
    if (grid.dataset.region !== S.region) {
        grid.textContent = '';
        grid.dataset.region = S.region;
        const fams = {};
        for (const m of MARKS) (fams[m.family] ||= []).push(m);
        for (const [fam, items] of Object.entries(fams)) {
            const sec = el('div', 'lc-fam');
            sec.append(el('h3', 'lc-fam-h', t('fam_' + fam)));
            const row = el('div', 'lc-fam-grid');
            for (const m of items) {
                const card = el('button', 'lc-mark');
                card.type = 'button'; card.dataset.id = m.id;
                const cv = el('canvas', 'lc-markcanvas');
                cv.dataset.id = m.id; cv.width = 260; cv.height = 260;
                card.append(cv);
                card.append(el('div', 'lc-mark-n', MT[m.id]?.name || m.id));
                card.append(el('div', 'lc-mark-c', characteristicFor(m, S.region) || ''));
                if (regionMatters(m)) card.append(el('div', 'lc-mark-r', t('region_dependent')));
                card.addEventListener('click', () => { S.markId = m.id; renderMarkDetail(); });
                row.append(card);
            }
            sec.append(row);
            grid.append(sec);
        }
    } else {
        for (const c of grid.querySelectorAll('.lc-mark')) {
            const m = BY_ID[c.dataset.id];
            c.querySelector('.lc-mark-c').textContent = characteristicFor(m, S.region) || '';
            c.querySelector('canvas')._parsed = null;
        }
    }
    for (const c of grid.querySelectorAll('.lc-mark')) c.classList.toggle('on', c.dataset.id === S.markId);
    renderMarkDetail();
    kick();
}

function renderMarkDetail() {
    const m = BY_ID[S.markId];
    if (!m) return;
    for (const c of document.querySelectorAll('#lcMarkGrid .lc-mark')) c.classList.toggle('on', c.dataset.id === S.markId);
    $('lcDetailName').textContent = MT[m.id]?.name || m.id;
    $('lcDetailChar').textContent = characteristicFor(m, S.region) || '';
    $('lcDetailText').textContent = MT[m.id]?.desc || '';
    const p = parseCharacteristic(characteristicFor(m, S.region) || '');
    $('lcDetailPlain').textContent = p.ok ? describe(p) : '';
    const alt = $('lcDetailAlt');
    if (m.altLight) {
        const ap = parseCharacteristic(m.altLight);
        alt.textContent = t('also_used', { c: m.altLight, plain: ap.ok ? describe(ap) : '' });
        alt.hidden = false;
    } else alt.hidden = true;
    const rd = $('lcDetailRegion');
    rd.textContent = regionMatters(m) ? t('region_note_yes') : t('region_note_no');
}

/* ═══ ТРЕНАЖЁР ═══════════════════════════════════════════════════════════ */
function newQuiz() {
    const pool = MARKS.filter(m => m.family !== 'shore');
    const mark = pool[Math.floor(Math.random() * pool.length)];
    const region = S.region;
    const parsed = parseCharacteristic(characteristicFor(mark, region));
    // Отвлекающие — из того же семейства: кардинальные путают между собой, а не
    // с маяком, и вариант «маяк» рядом с четырьмя буями решается без знания.
    const sameFam = pool.filter(m => m.family === mark.family && m.id !== mark.id);
    const others = pool.filter(m => m.family !== mark.family && m.id !== mark.id);
    const pick = [...shuffle(sameFam).slice(0, 3), ...shuffle(others)].slice(0, 3);
    S.quiz = { mark, region, parsed, revealed: false, options: shuffle([mark, ...pick]) };
    S.t0 = performance.now();

    $('lcQuizAnswers').textContent = '';
    for (const o of S.quiz.options) {
        const b = el('button', 'lc-ans');
        b.type = 'button'; b.dataset.id = o.id;
        b.textContent = MT[o.id]?.name || o.id;
        b.addEventListener('click', () => answerQuiz(o));
        $('lcQuizAnswers').append(b);
    }
    $('lcQuizVerdict').hidden = true;
    $('lcQuizNext').hidden = true;
    $('lcQuizRhythmWrap').hidden = true;
    $('lcQuizChar').textContent = '';
    kick();
}
function shuffle(a) { const x = [...a]; for (let i = x.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [x[i], x[j]] = [x[j], x[i]]; } return x; }

function answerQuiz(opt) {
    if (S.quiz.revealed) return;
    S.quiz.revealed = true;
    S.quizStats.asked++;
    const right = opt.id === S.quiz.mark.id;
    if (right) S.quizStats.right++;
    for (const b of $('lcQuizAnswers').children) {
        b.disabled = true;
        if (b.dataset.id === S.quiz.mark.id) b.classList.add('ok');
        else if (b.dataset.id === opt.id) b.classList.add('no');
        else b.classList.add('dim');
    }
    const v = $('lcQuizVerdict');
    v.hidden = false;
    v.className = 'lc-verdict ' + (right ? 'ok' : 'no');
    v.textContent = '';
    v.append(el('div', 'lc-v-head', right ? t('right') : t('wrong')));
    v.append(el('div', 'lc-v-name', MT[S.quiz.mark.id]?.name || ''));
    if (MT[S.quiz.mark.id]?.desc) v.append(el('p', 'tc-note', MT[S.quiz.mark.id].desc));
    $('lcQuizChar').textContent = characteristicFor(S.quiz.mark, S.quiz.region) || '';
    $('lcQuizRhythmWrap').hidden = false;
    $('lcQuizNext').hidden = false;
    $('lcQuizStats').textContent = t('stats', S.quizStats);
    window.__announce?.(right ? t('right') : t('wrong'));
    window.__aeFunnel?.('answer', { id: S.quiz.mark.id, correct: right });
}

/* ── режимы и подключение ────────────────────────────────────────────────── */
function setMode(m) {
    S.mode = m;
    for (const b of document.querySelectorAll('.tc-seg-b')) b.classList.toggle('on', b.dataset.mode === m);
    for (const p of document.querySelectorAll('.lc-pane')) p.hidden = p.dataset.mode !== m;
    if (m === 'decode') renderDecode();
    if (m === 'marks') renderMarks();
    if (m === 'quiz' && !S.quiz) newQuiz();
    kick();
    window.__stage?.('mode_' + m);
}

function bind() {
    for (const b of document.querySelectorAll('.tc-seg-b')) b.addEventListener('click', () => setMode(b.dataset.mode));

    const inp = $('lcInput');
    let deb;
    inp.addEventListener('input', () => {
        S.input = inp.value.trim();
        clearTimeout(deb);
        deb = setTimeout(() => { S.t0 = performance.now(); renderDecode(); }, 180);
    });
    for (const b of document.querySelectorAll('.tc-chip')) {
        b.addEventListener('click', () => {
            inp.value = b.dataset.v; S.input = b.dataset.v; S.t0 = performance.now(); renderDecode();
        });
    }
    $('lcEye').addEventListener('input', e => {
        S.eye = Number(e.target.value);
        $('lcEyeOut').textContent = t('eye_out', { n: S.eye, h: fmt(horizonNm(S.eye)) });
        if (S.parsed?.ok) renderRange(S.parsed);
    });
    $('lcVis').addEventListener('input', e => {
        S.metVis = Number(e.target.value);
        $('lcVisOut').textContent = t('vis_out', { n: S.metVis });
        if (S.parsed?.ok) renderRange(S.parsed);
    });
    for (const r of document.querySelectorAll('.lc-region')) {
        r.addEventListener('change', () => {
            S.region = document.querySelector('.lc-region:checked').value;
            window.__toolSettings?.save('light-characteristics', 'region', S.region);
            if (S.mode === 'marks') renderMarks();
            if (S.mode === 'quiz') newQuiz();
        });
    }
    $('lcNight').addEventListener('change', e => { S.night = e.target.checked; kick(); });
    $('lcQuizNext').addEventListener('click', newQuiz);
}

function boot() {
    try {
        bind();
        const saved = window.__toolSettings?.load('light-characteristics', 'region', 'A');
        S.region = saved === 'B' ? 'B' : 'A';
        const rr = document.querySelector(`.lc-region[value="${S.region}"]`);
        if (rr) rr.checked = true;
        $('lcInput').value = S.input;
        $('lcEyeOut').textContent = t('eye_out', { n: S.eye, h: fmt(horizonNm(S.eye)) });
        $('lcVisOut').textContent = t('vis_out', { n: S.metVis });
        setMode('decode');
        window.__stage?.('ready');
    } catch (err) {
        window.__handleError?.(err, 'light-characteristics');
        const b = $('lcResult');
        if (b) b.textContent = t('load_error');
    }
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();
